import { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

interface AccountPasswordModalProps {
  accountPasswordInfo: {
    email: string;
    account_name: string;
    temp_password: string;
  } | null;
  onClose: () => void;
}

export function AccountPasswordModal({
  accountPasswordInfo,
  onClose,
}: AccountPasswordModalProps) {
  const [showPassword, setShowPassword] = useState(false);

  const handleClose = () => {
    setShowPassword(false);
    onClose();
  };

  return (
    <Dialog
      open={accountPasswordInfo !== null}
      onClose={(_event, reason) => {
        if (reason === "backdropClick") return;
        handleClose();
      }}
      maxWidth="sm"
      fullWidth
      data-testid="account-password-modal"
    >
      <DialogTitle>アカウント登録完了 — 初期パスワード</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          以下の情報を安全な場所に保存してください。初期パスワードは初回ログイン時に変更が必要です。
        </Typography>
        {[
          { label: "アカウント名", value: accountPasswordInfo?.account_name || "", masked: false },
          { label: "メールアドレス", value: accountPasswordInfo?.email || "", masked: false },
          { label: "初期パスワード", value: accountPasswordInfo?.temp_password || "", masked: true },
        ].map((item) => (
          <Box key={item.label} sx={{ display: "flex", alignItems: "center", mb: 1.5, py: 0.5 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {item.label}
              </Typography>
              <Typography variant="body1" sx={{ fontFamily: "monospace" }}>
                {item.masked && !showPassword ? "●●●●●●●●" : item.value}
              </Typography>
            </Box>
            {item.masked && (
              <Tooltip title={showPassword ? "パスワードを隠す" : "パスワードを表示"}>
                <IconButton
                  onClick={() => setShowPassword((prev) => !prev)}
                  size="small"
                  sx={{ ml: 0.5 }}
                  data-testid="account-password-toggle"
                >
                  {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ))}
        <Button
          variant="outlined"
          startIcon={<ContentCopyIcon />}
          fullWidth
          sx={{ mt: 1 }}
          data-testid="account-password-copy-all"
          onClick={() => {
            const text = `アカウント名: ${accountPasswordInfo?.account_name || ""}\nメールアドレス: ${accountPasswordInfo?.email || ""}\n初期パスワード: ${accountPasswordInfo?.temp_password || ""}`;
            navigator.clipboard.writeText(text);
          }}
        >
          すべてコピー
        </Button>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} data-testid="account-password-close-button">閉じる</Button>
      </DialogActions>
    </Dialog>
  );
}
